import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
  desktopContentOffsetClass,
  SIDEBAR_WIDTH_CLASS,
  SUB_SIDEBAR_LEFT_CLASS
} from '@/constants/layout';
import useUserSetting from '@/hooks/useUserSetting';

export interface SidebarLayoutContextInterface {
  /** True when the rails are minimized to icons. One setting drives both of them. */
  railsCollapsed: boolean;
  toggleRailsCollapsed: () => void;
  /** Set by the mounted section rail, so the content offset can make room for it. */
  subRailVisible: boolean;
  setSubRailVisible: (visible: boolean) => void;
  /** Tailwind classes derived from the state above. */
  railWidthClass: string;
  subRailLeftClass: string;
  contentOffsetClass: string;
}

const SidebarLayoutContext = createContext<SidebarLayoutContextInterface | undefined>(undefined);

const layoutClasses = (collapsed: boolean, subRailVisible: boolean) => ({
  railWidthClass: collapsed ? SIDEBAR_WIDTH_CLASS.collapsed : SIDEBAR_WIDTH_CLASS.expanded,
  subRailLeftClass: collapsed ? SUB_SIDEBAR_LEFT_CLASS.collapsed : SUB_SIDEBAR_LEFT_CLASS.expanded,
  contentOffsetClass: desktopContentOffsetClass({ collapsed, subRailVisible })
});

/**
 * Tracks the state of the desktop nav rails. It lives above the router so the rails, the page
 * content and the footer all agree on how much room the rails are taking up.
 *
 * Collapsing is deliberately one setting rather than one per rail: two independent toggles sat
 * side by side and made the chrome read as two unrelated widgets.
 */
export const SidebarLayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [railsCollapsed, setRailsCollapsed] = useUserSetting('sidebarCollapsed', false);
  const [subRailVisible, setSubRailVisible] = useState(false);

  const toggleRailsCollapsed = useCallback(
    () => setRailsCollapsed(!railsCollapsed),
    [railsCollapsed, setRailsCollapsed]
  );

  const value = useMemo(
    () => ({
      railsCollapsed,
      toggleRailsCollapsed,
      subRailVisible,
      setSubRailVisible,
      ...layoutClasses(railsCollapsed, subRailVisible)
    }),
    [railsCollapsed, subRailVisible, toggleRailsCollapsed]
  );

  return <SidebarLayoutContext.Provider value={value}>{children}</SidebarLayoutContext.Provider>;
};

/**
 * Returns the rail state. Falls back to a fully expanded workspace-only layout when no provider
 * is mounted, so components stay renderable in isolation (tests, Storybook).
 */
export const useSidebarLayout = (): SidebarLayoutContextInterface => {
  const context = useContext(SidebarLayoutContext);

  return (
    context ?? {
      railsCollapsed: false,
      toggleRailsCollapsed: () => {},
      subRailVisible: false,
      setSubRailVisible: () => {},
      ...layoutClasses(false, false)
    }
  );
};

export default SidebarLayoutContext;
